import { Input, VStack, Text, HStack } from "@chakra-ui/react"
import { Field } from "@hydrologiq/shared/chakra/Field"
import { Asset, AssetClass } from "@hydrologiq/gql-experience/graphql"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import Form from "@hydrologiq/shared/chakra/custom/Form"

interface AssetEditProps {
  asset: Asset
  onUpdate: (asset: Asset) => void
}

import { useState } from "react"
import AssetPopover from "@hydrologiq/shared/chakra/Popover"
import Dialog from "@hydrologiq/shared/chakra/Dialog"
import { ArchiveButton, CancelButton } from "@hydrologiq/shared/content/components/Button"
import { DEFAULT_COPY_MAPPINGS } from "../copy/constants"
import { COPY_KEYS } from "../copy/constants"
import { useCopyValues } from "../providers/helpers/useCopy"
import { useMutationWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import { showSuccessToast, showErrorToast } from "@hydrologiq/shared/utils/toast"

const AssetEdit = ({ asset, onUpdate }: AssetEditProps) => {
  const [open, setOpen] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [updateAsset, { loading }] = useMutationWithEndpoint(
    gqlTypes.UpdateAssetDocument,
    {
      refetchQueries: [gqlTypes.AssetAvailabilityCalendarDocument],
      awaitRefetchQueries: true,
    },
    "primary"
  )
  const [deleteAsset, { loading: deleteLoading }] = useMutationWithEndpoint(
    gqlTypes.DeleteAssetDocument,
    {
      refetchQueries: [gqlTypes.AssetAvailabilityCalendarDocument],
      awaitRefetchQueries: true,
      update: (cache) => {
        // Evict the deleted asset from cache
        cache.evict({ id: cache.identify({ __typename: "Asset", id: asset.id }) })

        // Also remove the asset from all AssetAvailabilityCalendar query results
        cache.modify({
          fields: {
            calendar(existingCalendar, { readField }) {
              if (!existingCalendar?.data) return existingCalendar

              // Filter out the deleted asset from the data array
              const filteredData = existingCalendar.data.filter((item: any) => {
                const assetId = readField("id", readField("asset", item))
                return assetId !== asset.id
              })

              return {
                ...existingCalendar,
                data: filteredData,
                meta: {
                  ...existingCalendar.meta,
                  total_assets: Math.max(0, (existingCalendar.meta?.total_assets || 0) - 1),
                },
              }
            },
          },
        })

        cache.gc() // Clean up orphaned references
      },
    },
    "primary"
  )
  const copy = useCopyValues({
    [COPY_KEYS.HEADINGS.ASSET]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ASSET],
  })
  const assetCopy = copy[COPY_KEYS.HEADINGS.ASSET].toLocaleLowerCase()

  const handleDelete = async () => {
    try {
      const result = await deleteAsset({ variables: { id: asset.id } })

      // Check for GraphQL errors first
      if (result.errors && result.errors.length > 0) {
        const errorMessage = result.errors[0].message || "Failed to delete asset. Please try again."
        showErrorToast("Delete Failed", errorMessage)
        return
      }

      if (result.data?.deleteAsset) {
        showSuccessToast("Asset Deleted", "Asset has been successfully deleted.")
        onUpdate(asset)
        setShowDeleteConfirm(false)
        setOpen(false)
      } else {
        showErrorToast("Delete Failed", "Failed to delete asset. The server returned an unexpected response.")
      }
    } catch (error: any) {
      const errorMessage = error?.message || "Unable to delete asset. Please try again."
      showErrorToast("Delete Failed", errorMessage)
    }
  }

  return (
    <>
      <Dialog
        title={`Delete ${assetCopy}`}
        open={showDeleteConfirm}
        setOpen={setShowDeleteConfirm}
        footer={
          <HStack justifyContent="space-between" width="100%">
            <CancelButton disabled={deleteLoading} onClick={() => setShowDeleteConfirm(false)} />
            <ArchiveButton
              onClick={handleDelete}
              disabled={deleteLoading}
              loading={deleteLoading}
              context={{ label: "Delete", description: "Confirm the deletion of the asset", variant: "warning" }}
            />
          </HStack>
        }
        loading={deleteLoading}
        disabled={deleteLoading}
      >
        <Text>Are you sure you want to delete this {assetCopy}?</Text>
      </Dialog>
      <AssetPopover
        iconType="edit"
        actionContent={{ label: "Edit", description: "Edit the asset" }}
        placement="bottom"
        loading={loading || deleteLoading}
        onOpenChange={setOpen}
        open={open}
        width="200px"
      >
        <Form
          heading={`Edit the ${copy[COPY_KEYS.HEADINGS.ASSET].toLocaleLowerCase()}`}
          helperText={`Please provide updated ${copy[COPY_KEYS.HEADINGS.ASSET].toLocaleLowerCase()} information below.`}
          actionType="update"
          actionContent={{ label: "Update", description: "Update the asset" }}
          loading={loading || deleteLoading}
          secondaryActionType="archive"
          secondaryActionContent={{ label: "Delete", description: "Delete the asset" }}
          onSecondaryAction={() => {
            setOpen(false)
            setShowDeleteConfirm(true)
            return true
          }}
          onSubmit={async (data: FormData) => {
            const name = data.get("name")?.toString()
            const volume = data.get("volume") ? parseFloat(data.get("volume")?.toString() || "0") : undefined
            const min_pressure = data.get("min_pressure")
              ? parseFloat(data.get("min_pressure")?.toString() || "0")
              : undefined
            if (name) {
              try {
                const result = await updateAsset({
                  variables: { id: asset.id, name, volume, min_pressure },
                })

                // Check for GraphQL errors first
                if (result.errors && result.errors.length > 0) {
                  const errorMessage = result.errors[0].message || "Failed to update asset. Please try again."
                  showErrorToast("Update Failed", errorMessage)
                  return false
                }

                if (result.data?.updateAsset) {
                  showSuccessToast("Asset Updated", "Asset has been successfully updated.")
                  onUpdate(result.data.updateAsset)
                  setOpen(false)
                  return true
                } else {
                  showErrorToast("Update Failed", "Failed to update asset. The server returned an unexpected response.")
                  return false
                }
              } catch (error: any) {
                const errorMessage = error?.message || "Unable to update asset. Please try again."
                showErrorToast("Update Failed", errorMessage)
                return false
              }
            }
            return false
          }}
          onCancel={() => setOpen(false)}
        >
          <VStack gap={5}>
            <Field label="Name" required>
              <Input name="name" defaultValue={asset.name} />
            </Field>

            {asset.class === AssetClass.Fuel && (
              <>
                <Field label="Volume (m³)">
                  <Input name="volume" defaultValue={asset.volume?.toString() || ""} />
                </Field>

                <Field label="Minimum Operating Pressure (bar)">
                  <Input name="min_pressure" defaultValue={asset.min_pressure?.toString() || ""} />
                </Field>
              </>
            )}
          </VStack>
        </Form>
      </AssetPopover>
    </>
  )
}

export default AssetEdit
